import { useMemo, useState } from "react";
import { differenceInDays, parseISO } from "date-fns";
import type { TransferConnection } from "@/hooks/useVesselAssignments";
import type { Vessel } from "@/hooks/useVessels";
import { getConnectionPath } from "./ConnectionContext";
import { getVarietyColor, VARIETY_DEFAULT_COLOR } from "@/hooks/useVarietyColorMap";

// SVG path for arrow/transfer icon (simplified from Lucide ArrowRight)
const TRANSFER_ICON_PATH = "M5 12h14M12 5l7 7-7 7";
// SVG path for X icon (delete affordance on hover)
const DELETE_ICON_PATH = "M18 6L6 18M6 6l12 12";

interface TransferConnectionLinesProps {
  connections: TransferConnection[];
  vessels: Vessel[];
  startDate: Date;
  endDate: Date;
  dayWidth: number;
  rowHeight: number;
  vesselLabelWidth: number;
  colorMap?: Record<string, string>;
  onDeleteConnection?: (connectionId: string) => void;
}

export function TransferConnectionLines({
  connections,
  vessels,
  startDate,
  endDate,
  dayWidth,
  rowHeight,
  vesselLabelWidth,
  colorMap = {},
  onDeleteConnection,
}: TransferConnectionLinesProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  // Create a map of vessel ID to row index for Y position calculation
  const vesselIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    vessels.forEach((v, idx) => {
      map.set(v.id, idx);
    });
    return map;
  }, [vessels]);

  // Calculate positions for each connection
  const connectionPaths = useMemo(() => {
    return connections.map((conn) => {
      // Get vessel row indices
      const sourceRowIndex = vesselIndexMap.get(conn.source_vessel_id);
      const targetRowIndex = vesselIndexMap.get(conn.target_vessel_id);

      // Skip if vessels aren't visible
      if (sourceRowIndex === undefined || targetRowIndex === undefined) {
        return null;
      }

      // Calculate X positions to match AssignmentBar positioning
      // Source: connect from the RIGHT EDGE (end) of the source bar
      // Target: connect to the LEFT EDGE (start) of the target bar

      // When source has no empty date, bar extends to timeline endDate (matches AssignmentBar)
      const sourceEmptyDate = conn.source_empty_date
        ? parseISO(conn.source_empty_date)
        : endDate;
      const targetFillDate = parseISO(conn.target_fill_date);

      // Source bar right edge: compute directly from emptyDate relative to startDate.
      // AssignmentBar calculates barRightPx = rawLeft + width where
      //   rawLeft = (fillDate - startDate) * dayWidth  (can be negative when left-clipped)
      //   width   = (emptyDate - fillDate + 1) * dayWidth
      //   barRightPx = (emptyDate - startDate + 1) * dayWidth  (independent of fillDate clipping)
      // Using (sourceDaysFromStart + sourceDaysUntilEnd) would overshoot by |clippedDays| when
      // fillDate precedes startDate because sourceDaysFromStart is clamped to 0 but
      // sourceDaysUntilEnd still uses the full span from the original fillDate.
      const sourceRightDay = differenceInDays(sourceEmptyDate, startDate) + 1;
      const sourceX = vesselLabelWidth + Math.max(0, sourceRightDay) * dayWidth;

      // Target bar left edge — clamping handles left-clipped target bars correctly
      const targetDaysFromStart = Math.max(0, differenceInDays(targetFillDate, startDate));
      const targetX = vesselLabelWidth + targetDaysFromStart * dayWidth;

      // Y positions: center of each row
      // SVG is positioned inside body rows container (no header offset needed)
      const sourceY = sourceRowIndex * rowHeight + rowHeight / 2;
      const targetY = targetRowIndex * rowHeight + rowHeight / 2;

      // Generate bezier path
      const path = getConnectionPath(sourceX, sourceY, targetX, targetY);

      // Calculate midpoint for icon placement
      const midX = (sourceX + targetX) / 2;
      const midY = (sourceY + targetY) / 2;

      // VV code from lot code is most reliable; fall back to stored varietal, then assignment color
      const connVvCode = conn.lot_code && conn.lot_code.length >= 6 ? conn.lot_code.slice(4, 6).toLowerCase() : null;
      const varietyColor = (connVvCode && colorMap[connVvCode])
        ? colorMap[connVvCode]
        : getVarietyColor(colorMap, conn.varietal);
      const lineColor = varietyColor !== VARIETY_DEFAULT_COLOR
        ? varietyColor
        : (conn.color || VARIETY_DEFAULT_COLOR);

      return {
        id: `${conn.source_assignment_id}-${conn.target_assignment_id}`,
        connectionId: conn.connection_id,
        path,
        sourceX,
        sourceY,
        targetX,
        targetY,
        midX,
        midY,
        lineColor,
        connection: conn,
      };
    }).filter(Boolean);
  }, [connections, vesselIndexMap, startDate, endDate, dayWidth, rowHeight, vesselLabelWidth]);

  if (connectionPaths.length === 0) {
    return null;
  }

  return (
    <svg
      className="transfer-lines-svg absolute inset-0 pointer-events-none z-10 overflow-visible"
      style={{ width: "100%", height: "100%" }}
    >
      {/* Definitions for markers and animations */}
      <defs>
        {/* Create a unique arrow marker for each connection color */}
        {connectionPaths.map((cp) => cp && (
          <marker
            key={`marker-${cp.id}`}
            id={`transfer-arrow-${cp.id}`}
            markerWidth="8"
            markerHeight="8"
            refX="7"
            refY="4"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M0,1 L0,7 L7,4 z" fill={cp.lineColor} fillOpacity={0.7} />
          </marker>
        ))}

        {/* Animated dash keyframes */}
        <style>
          {`
            @keyframes flowDash {
              to {
                stroke-dashoffset: -18;
              }
            }
            .transfer-line-animated {
              animation: flowDash 1.5s linear infinite;
            }
          `}
        </style>
      </defs>

      {connectionPaths.map((cp) => {
        if (!cp) return null;

        return (
          <g key={cp.id}>
            {/* Shadow/glow effect */}
            <path
              d={cp.path}
              fill="none"
              stroke={cp.lineColor}
              strokeWidth={6}
              strokeOpacity={0.15}
              strokeLinecap="round"
            />

            {/* Main connection line with animation */}
            <path
              d={cp.path}
              fill="none"
              stroke={cp.lineColor}
              strokeWidth={2.5}
              strokeOpacity={0.7}
              strokeLinecap="round"
              strokeDasharray="8 4"
              markerEnd={`url(#transfer-arrow-${cp.id})`}
              className="transfer-line-animated"
            />

            {/* Source dot */}
            <circle
              cx={cp.sourceX}
              cy={cp.sourceY}
              r={5}
              fill={cp.lineColor}
              fillOpacity={0.8}
              stroke="white"
              strokeWidth={2}
            />

            {/* Target dot */}
            <circle
              cx={cp.targetX}
              cy={cp.targetY}
              r={5}
              fill={cp.lineColor}
              fillOpacity={0.8}
              stroke="white"
              strokeWidth={2}
            />

            {/* Transfer icon at midpoint — clickable to delete */}
            <g
              transform={`translate(${cp.midX - 12}, ${cp.midY - 12})`}
              pointerEvents={onDeleteConnection ? "all" : "none"}
              style={{ cursor: onDeleteConnection ? "pointer" : "default" }}
              onMouseEnter={() => setHoveredId(cp.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={(e) => {
                if (!onDeleteConnection) return;
                e.stopPropagation();
                onDeleteConnection(cp.connectionId);
              }}
            >
              <circle
                cx={12}
                cy={12}
                r={12}
                fill="white"
                stroke={hoveredId === cp.id ? "#ef4444" : cp.lineColor}
                strokeWidth={hoveredId === cp.id ? 2.5 : 2}
                strokeOpacity={0.9}
              />
              {/* Show X on hover, arrow icon otherwise */}
              <g transform="translate(6, 6)">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path
                    d={hoveredId === cp.id ? DELETE_ICON_PATH : TRANSFER_ICON_PATH}
                    stroke={hoveredId === cp.id ? "#ef4444" : cp.lineColor}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </g>
            </g>
          </g>
        );
      })}
    </svg>
  );
}
